import { useState, useEffect } from 'react'
import { X, Trash2, Box, Grid2x2, Pencil, Save, XCircle, Plus } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { GraphNode, GraphLink, EntityType, Note, AINote, Person, Project, Task, Mood, Body, Food, Activity, Resource, Reflection } from '../types'
import { ENTITY_COLORS, getRelationshipColor } from '../lib/graph-utils'

const TASK_STATUSES = ['todo', 'pending', 'backlog', 'in_progress', 'completed', 'deprecated', 'cancelled'] as const
const PROJECT_STATUSES = ['active', 'completed', 'paused', 'archived'] as const
const AI_NOTE_TYPES = ['insight', 'summary', 'synthesis', 'question', 'action_item'] as const

const RELATIONSHIP_OPTIONS = [
  'RELATED_TO', 'MENTIONS', 'BELONGS_TO', 'PART_OF', 'CONTAINS',
  'DEPENDS_ON', 'CREATED', 'HAS_TASK', 'HAS_MOOD', 'HAS_BODY',
  'HAS_FOOD', 'HAS_ACTIVITY', 'HAS_RESOURCE', 'HAS_REFLECTION',
]

const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  note: 'Note', ai_note: 'AI Note', person: 'Person', project: 'Project', task: 'Task',
  mood: 'Mood', body: 'Body', food: 'Food', activity: 'Activity',
  resource: 'Resource', reflection: 'Reflection',
}

interface NodeCardProps {
  node: GraphNode
  links: GraphLink[]
  nodes: GraphNode[]
  onClose: () => void
  onNodeSelect: (node: GraphNode) => void
  onDelete?: () => void
  onShowOnGraph?: (mode: '3d' | '2d') => void
  onTaskStatusChange?: (status: string) => void
  onTaskPriorityChange?: (priority: number) => void
  onEntityUpdate?: (updates: Record<string, any>) => void
  onEdgeCreate?: (edge: { from_type: string; from_id: number; to_type: string; to_id: number; relationship: string }) => void
  onEdgeDelete?: (edgeId: number) => void
}

// Input components for edit mode
function TextInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-gray-500"
      />
    </div>
  )
}

function TextArea({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={4}
        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-gray-500 resize-y"
      />
    </div>
  )
}

function NumberInput({ label, value, onChange, min, max, step }: { label: string; value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number }) {
  return (
    <div>
      <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      <input
        type="number"
        value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        min={min}
        max={max}
        step={step ?? 0.1}
        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-gray-500"
      />
    </div>
  )
}

function SelectInput({ label, value, options, onChange }: { label: string; value: string; options: readonly string[]; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-gray-500"
      >
        {options.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
      </select>
    </div>
  )
}

function ArrayInput({ label, value, onChange }: { label: string; value: string[]; onChange: (v: string[]) => void }) {
  const [text, setText] = useState(value.join(', '))
  useEffect(() => { setText(value.join(', ')) }, [value])
  return (
    <div>
      <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1">{label} (comma-separated)</label>
      <input
        type="text"
        value={text}
        onChange={e => {
          setText(e.target.value)
          onChange(e.target.value.split(',').map(s => s.trim()).filter(Boolean))
        }}
        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-gray-500"
      />
    </div>
  )
}

function CheckboxInput({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={value}
        onChange={e => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-gray-600 bg-gray-900 text-blue-500 focus:ring-0"
      />
      <span className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</span>
    </label>
  )
}

function DateInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      <input
        type="date"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-gray-500"
      />
    </div>
  )
}

// Connection adder component
function ConnectionAdder({
  currentNode,
  allNodes,
  onAdd,
  onCancel,
}: {
  currentNode: GraphNode
  allNodes: GraphNode[]
  onAdd: (edge: { from_type: string; from_id: number; to_type: string; to_id: number; relationship: string }) => void
  onCancel: () => void
}) {
  const [targetType, setTargetType] = useState<EntityType>('note')
  const [search, setSearch] = useState('')
  const [selectedTarget, setSelectedTarget] = useState<GraphNode | null>(null)
  const [relationship, setRelationship] = useState('RELATED_TO')

  const candidates = allNodes.filter(n =>
    n.id !== currentNode.id &&
    n.entityType === targetType &&
    (search ? n.label.toLowerCase().includes(search.toLowerCase()) : true)
  ).slice(0, 20)

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400 font-medium">Add Connection</span>
        <button onClick={onCancel} className="text-gray-500 hover:text-white">
          <X size={14} />
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {(Object.keys(ENTITY_TYPE_LABELS) as EntityType[]).map(t => (
          <button
            key={t}
            onClick={() => { setTargetType(t); setSelectedTarget(null) }}
            className={`px-2 py-0.5 rounded text-[10px] font-medium border transition-colors ${
              targetType === t
                ? 'border-gray-500 text-white bg-gray-800'
                : 'border-gray-800 text-gray-600 hover:text-gray-400'
            }`}
          >
            {ENTITY_TYPE_LABELS[t]}
          </button>
        ))}
      </div>

      <input
        type="text"
        placeholder="Search..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white focus:outline-none focus:border-gray-500"
      />

      <div className="max-h-32 overflow-y-auto space-y-0.5">
        {candidates.map(n => (
          <button
            key={n.id}
            onClick={() => setSelectedTarget(n)}
            className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${
              selectedTarget?.id === n.id ? 'bg-gray-700 text-white' : 'text-gray-400 hover:bg-gray-800'
            }`}
          >
            <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: ENTITY_COLORS[n.entityType] }} />
            {n.label.slice(0, 50)}
          </button>
        ))}
        {candidates.length === 0 && <div className="text-xs text-gray-600 py-2 text-center">No matches</div>}
      </div>

      <select
        value={relationship}
        onChange={e => setRelationship(e.target.value)}
        className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white focus:outline-none"
      >
        {RELATIONSHIP_OPTIONS.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
      </select>

      <button
        disabled={!selectedTarget}
        onClick={() => {
          if (!selectedTarget) return
          onAdd({
            from_type: currentNode.entityType,
            from_id: currentNode.entityId,
            to_type: selectedTarget.entityType,
            to_id: selectedTarget.entityId,
            relationship,
          })
        }}
        className="w-full py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-30 disabled:hover:bg-blue-600 text-white text-xs font-medium rounded transition-colors"
      >
        Add Connection
      </button>
    </div>
  )
}

export default function NodeCard({
  node, links, nodes, onClose, onNodeSelect, onDelete, onShowOnGraph,
  onTaskStatusChange, onTaskPriorityChange, onEntityUpdate, onEdgeCreate, onEdgeDelete,
}: NodeCardProps) {
  const [deleteStep, setDeleteStep] = useState(0)
  const [editing, setEditing] = useState(false)
  const [editData, setEditData] = useState<Record<string, any>>({})
  const [addingConnection, setAddingConnection] = useState(false)

  // Auto-reset confirm state after 3 seconds
  useEffect(() => {
    if (deleteStep === 0) return
    const t = setTimeout(() => setDeleteStep(0), 3000)
    return () => clearTimeout(t)
  }, [deleteStep])

  // Reset edit state when node changes
  useEffect(() => {
    setEditing(false)
    setEditData({})
    setAddingConnection(false)
  }, [node.id])

  const color = ENTITY_COLORS[node.entityType]

  const startEditing = () => {
    setEditData({ ...node.data as any })
    setEditing(true)
  }

  const cancelEditing = () => {
    setEditing(false)
    setEditData({})
  }

  const saveEdits = () => {
    if (!onEntityUpdate) return
    // Compute diff — only send changed fields
    const original = node.data as any
    const updates: Record<string, any> = {}
    for (const key of Object.keys(editData)) {
      if (JSON.stringify(editData[key]) !== JSON.stringify(original[key])) {
        updates[key] = editData[key]
      }
    }
    if (Object.keys(updates).length > 0) {
      onEntityUpdate(updates)
    }
    setEditing(false)
    setEditData({})
  }

  const updateField = (field: string, value: any) => {
    setEditData(prev => ({ ...prev, [field]: value }))
  }

  // Find connections for this node
  const connections = links
    .filter(l => {
      const sourceId = typeof l.source === 'string' ? l.source : (l.source as any)?.id
      const targetId = typeof l.target === 'string' ? l.target : (l.target as any)?.id
      return sourceId === node.id || targetId === node.id
    })
    .map(l => {
      const sourceId = typeof l.source === 'string' ? l.source : (l.source as any)?.id
      const targetId = typeof l.target === 'string' ? l.target : (l.target as any)?.id
      const connectedId = sourceId === node.id ? targetId : sourceId
      const connectedNode = nodes.find(n => n.id === connectedId)
      return {
        node: connectedNode,
        relationship: l.relationship,
        direction: sourceId === node.id ? 'outgoing' : 'incoming',
        edgeId: l.edgeId,
      }
    })
    .filter(c => c.node)

  // Render edit fields based on entity type
  const renderEditFields = () => {
    const d = editData
    switch (node.entityType) {
      case 'note':
        return <TextArea label="Text" value={d.text || ''} onChange={v => updateField('text', v)} />
      case 'ai_note':
        return (
          <>
            <TextArea label="Content" value={d.content || ''} onChange={v => updateField('content', v)} />
            <SelectInput label="Note Type" value={d.note_type || 'insight'} options={AI_NOTE_TYPES} onChange={v => updateField('note_type', v)} />
            <ArrayInput label="Meta Tags" value={d.meta_tags || []} onChange={v => updateField('meta_tags', v)} />
          </>
        )
      case 'person':
        return (
          <>
            <TextInput label="Name" value={d.name || ''} onChange={v => updateField('name', v)} />
            <ArrayInput label="Aliases" value={d.aliases || []} onChange={v => updateField('aliases', v)} />
            <TextArea label="Context" value={d.context || ''} onChange={v => updateField('context', v)} />
          </>
        )
      case 'project':
        return (
          <>
            <TextInput label="Name" value={d.name || ''} onChange={v => updateField('name', v)} />
            <TextArea label="Description" value={d.description || ''} onChange={v => updateField('description', v)} />
            <SelectInput label="Status" value={d.status || 'active'} options={PROJECT_STATUSES} onChange={v => updateField('status', v)} />
          </>
        )
      case 'task':
        return (
          <>
            <TextInput label="Title" value={d.title || ''} onChange={v => updateField('title', v)} />
            <TextArea label="Description" value={d.description || ''} onChange={v => updateField('description', v)} />
            <DateInput label="Due Date" value={d.due_at ? d.due_at.slice(0, 10) : ''} onChange={v => updateField('due_at', v || null)} />
          </>
        )
      case 'mood':
        return (
          <>
            <ArrayInput label="Emotions" value={d.emotions || []} onChange={v => updateField('emotions', v)} />
            <NumberInput label="Valence" value={d.valence ?? 0} onChange={v => updateField('valence', v)} min={-1} max={1} step={0.1} />
            <NumberInput label="Energy" value={d.energy ?? 0} onChange={v => updateField('energy', v)} min={0} max={1} step={0.1} />
            <TextInput label="Trigger" value={d.trigger || ''} onChange={v => updateField('trigger', v)} />
          </>
        )
      case 'body':
        return (
          <>
            <TextArea label="Physical" value={d.physical || ''} onChange={v => updateField('physical', v)} />
            <TextArea label="Sleep" value={d.sleep || ''} onChange={v => updateField('sleep', v)} />
            <TextArea label="Substance" value={d.substance || ''} onChange={v => updateField('substance', v)} />
          </>
        )
      case 'food':
        return (
          <>
            <ArrayInput label="Items" value={d.items || []} onChange={v => updateField('items', v)} />
            <TextInput label="Meal" value={d.meal || ''} onChange={v => updateField('meal', v)} />
            <TextArea label="Observation" value={d.observation || ''} onChange={v => updateField('observation', v)} />
          </>
        )
      case 'activity':
        return (
          <>
            <TextInput label="Activity Type" value={d.activity_type || ''} onChange={v => updateField('activity_type', v)} />
            <TextArea label="Description" value={d.description || ''} onChange={v => updateField('description', v)} />
            <TextInput label="Duration Estimate" value={d.duration_estimate || ''} onChange={v => updateField('duration_estimate', v)} />
            <TextInput label="Location" value={d.location || ''} onChange={v => updateField('location', v)} />
            <NumberInput label="Productivity Signal" value={d.productivity_signal ?? 0} onChange={v => updateField('productivity_signal', v)} min={0} max={1} step={0.1} />
          </>
        )
      case 'resource':
        return (
          <>
            <TextInput label="Title" value={d.title || ''} onChange={v => updateField('title', v)} />
            <TextInput label="URL" value={d.url || ''} onChange={v => updateField('url', v)} />
            <TextArea label="Description" value={d.description || ''} onChange={v => updateField('description', v)} />
            <TextInput label="Source Type" value={d.source_type || ''} onChange={v => updateField('source_type', v)} />
            <CheckboxInput label="Save Recommended" value={d.save_recommended || false} onChange={v => updateField('save_recommended', v)} />
          </>
        )
      case 'reflection':
        return (
          <>
            <TextArea label="Content" value={d.content || ''} onChange={v => updateField('content', v)} />
            <TextInput label="Domain" value={d.domain || ''} onChange={v => updateField('domain', v)} />
            <TextInput label="Actionability" value={d.actionability || ''} onChange={v => updateField('actionability', v)} />
          </>
        )
      default:
        return null
    }
  }

  // Get content based on entity type (read mode)
  const getContent = () => {
    switch (node.entityType) {
      case 'note': {
        const d = node.data as Note
        return {
          title: null,
          subtitle: new Date(d.timestamp).toLocaleDateString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
          }),
          content: d.text,
          meta: d.source_type ? [{ label: 'Source', value: d.source_type }] : []
        }
      }
      case 'ai_note': {
        const d = node.data as AINote
        return {
          title: d.note_type.toUpperCase(),
          subtitle: new Date(d.created_at).toLocaleDateString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
          }),
          content: d.content,
          meta: d.meta_tags?.length
            ? [{ label: 'Tags', value: d.meta_tags.map(t => `#${t}`).join(' ') }]
            : []
        }
      }
      case 'person': {
        const d = node.data as Person
        const meta: { label: string; value: string }[] = []
        if (d.aliases?.length) meta.push({ label: 'Aliases', value: d.aliases.join(', ') })
        return {
          title: d.name,
          subtitle: null,
          content: d.context || null,
          meta
        }
      }
      case 'project': {
        const d = node.data as Project
        return {
          title: d.name,
          subtitle: null,
          content: d.description || null,
          meta: [{ label: 'Status', value: d.status }]
        }
      }
      case 'task': {
        const d = node.data as Task
        const meta: { label: string; value: string }[] = []
        if (d.due_at) meta.push({ label: 'Due', value: new Date(d.due_at).toLocaleDateString() })
        if (d.type) meta.push({ label: 'Type', value: d.type })
        return {
          title: d.title,
          subtitle: null,
          content: d.description || null,
          meta
        }
      }
      case 'mood': {
        const d = node.data as Mood
        const meta: { label: string; value: string }[] = []
        if (d.valence != null) { const v = Number(d.valence); meta.push({ label: 'Valence', value: v > 0 ? `+${v.toFixed(2)}` : v.toFixed(2) }) }
        if (d.energy != null) meta.push({ label: 'Energy', value: Number(d.energy).toFixed(2) })
        if (d.confidence != null) meta.push({ label: 'Confidence', value: Number(d.confidence).toFixed(2) })
        return {
          title: 'Mood',
          subtitle: d.emotions?.join(', ') || null,
          content: d.trigger ? `Trigger: ${d.trigger}` : null,
          meta,
        }
      }
      case 'body': {
        const d = node.data as Body
        const parts: string[] = []
        if (d.physical) parts.push(`Physical: ${d.physical}`)
        if (d.sleep) parts.push(`Sleep: ${d.sleep}`)
        if (d.substance) parts.push(`Substance: ${d.substance}`)
        return { title: 'Body', subtitle: null, content: parts.join('\n') || null, meta: [] }
      }
      case 'food': {
        const d = node.data as Food
        const meta: { label: string; value: string }[] = []
        if (d.meal) meta.push({ label: 'Meal', value: d.meal })
        return {
          title: 'Food',
          subtitle: d.items?.join(', ') || null,
          content: d.observation || null,
          meta,
        }
      }
      case 'activity': {
        const d = node.data as Activity
        const meta: { label: string; value: string }[] = [
          { label: 'Type', value: d.activity_type },
        ]
        if (d.duration_estimate) meta.push({ label: 'Duration', value: d.duration_estimate })
        if (d.location) meta.push({ label: 'Location', value: d.location })
        if (d.productivity_signal != null) meta.push({ label: 'Productivity', value: Number(d.productivity_signal).toFixed(2) })
        return { title: d.description || d.activity_type, subtitle: null, content: null, meta }
      }
      case 'resource': {
        const d = node.data as Resource
        const meta: { label: string; value: string }[] = []
        if (d.source_type) meta.push({ label: 'Type', value: d.source_type })
        if (d.save_recommended) meta.push({ label: 'Saved', value: '*' })
        return {
          title: d.title || 'Resource',
          subtitle: d.url || null,
          content: d.description || null,
          meta,
        }
      }
      case 'reflection': {
        const d = node.data as Reflection
        const meta: { label: string; value: string }[] = []
        if (d.domain) meta.push({ label: 'Domain', value: d.domain })
        if (d.actionability) meta.push({ label: 'Action', value: d.actionability })
        return { title: 'Reflection', subtitle: null, content: d.content, meta }
      }
      default:
        return { title: null, subtitle: null, content: null, meta: [] }
    }
  }

  const { title, subtitle, content, meta } = getContent()

  return (
    <div className="fixed inset-0 z-50 bg-[#0a0a0a] overflow-y-auto"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* Header bar */}
      <div className="sticky top-0 z-10 bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-gray-800">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2.5">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
            <span
              className="text-xs uppercase tracking-widest font-medium"
              style={{ color }}
            >
              {node.entityType.replace('_', ' ')}
            </span>
            <span className="text-[10px] text-gray-600 font-mono">
              #{node.entityId}
            </span>
            {onShowOnGraph && (
              <>
                <button
                  onClick={() => onShowOnGraph('3d')}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
                >
                  <Box size={10} />
                  3D
                </button>
                <button
                  onClick={() => onShowOnGraph('2d')}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
                >
                  <Grid2x2 size={10} />
                  2D
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            {onEntityUpdate && !editing && (
              <button
                onClick={startEditing}
                className="p-1.5 text-gray-500 hover:text-blue-400 rounded-lg hover:bg-gray-800 transition-colors"
                title="Edit"
              >
                <Pencil size={16} />
              </button>
            )}
            {onDelete && !editing && (
              <button
                onClick={() => {
                  if (deleteStep >= 2) { onDelete() } else { setDeleteStep(s => s + 1) }
                }}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  deleteStep === 2
                    ? 'bg-red-700 text-white hover:bg-red-600'
                    : deleteStep === 1
                    ? 'bg-yellow-700 text-white hover:bg-yellow-600'
                    : 'text-gray-500 hover:text-red-400 hover:bg-gray-800'
                }`}
              >
                <Trash2 size={14} />
                {deleteStep === 1 && <span>Delete?</span>}
                {deleteStep === 2 && <span>Sure?</span>}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-5 space-y-5">
        {editing ? (
          /* ---- Edit Mode ---- */
          <>
            <div className="space-y-4">
              {renderEditFields()}
            </div>
            <div className="flex gap-2">
              <button
                onClick={saveEdits}
                className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-500 text-white text-xs font-medium rounded-lg transition-colors"
              >
                <Save size={14} />
                Save
              </button>
              <button
                onClick={cancelEditing}
                className="flex items-center gap-1.5 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-xs font-medium rounded-lg transition-colors"
              >
                <XCircle size={14} />
                Cancel
              </button>
            </div>
          </>
        ) : (
          /* ---- Read Mode ---- */
          <>
            {title && (
              <h2 className="text-white text-xl font-semibold leading-tight">{title}</h2>
            )}

            {subtitle && (
              <div className="text-gray-500 text-sm">{subtitle}</div>
            )}

            {content && (
              <div className="text-gray-300 text-sm leading-relaxed markdown-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({...props}) => <h1 className="text-xl font-bold text-white mt-4 mb-2" {...props} />,
                    h2: ({...props}) => <h2 className="text-lg font-semibold text-white mt-3 mb-2" {...props} />,
                    h3: ({...props}) => <h3 className="text-base font-semibold text-white mt-2 mb-1" {...props} />,
                    h4: ({...props}) => <h4 className="text-sm font-semibold text-white mt-2 mb-1" {...props} />,
                    h5: ({...props}) => <h5 className="text-sm font-medium text-white mt-1 mb-1" {...props} />,
                    h6: ({...props}) => <h6 className="text-xs font-medium text-gray-300 mt-1 mb-1" {...props} />,
                    p: ({...props}) => <p className="mb-3" {...props} />,
                    ul: ({...props}) => <ul className="list-disc ml-6 mb-3" {...props} />,
                    ol: ({...props}) => <ol className="list-decimal ml-6 mb-3" {...props} />,
                    li: ({...props}) => <li className="mb-1" {...props} />,
                    a: ({...props}) => <a className="text-blue-400 hover:text-blue-300 underline" {...props} />,
                    strong: ({...props}) => <strong className="font-semibold text-white" {...props} />,
                    em: ({...props}) => <em className="italic" {...props} />,
                    code: ({...props}) => (
                      <code className="bg-gray-800 p-1 rounded text-sm font-mono" {...props} />
                    ),
                    blockquote: ({...props}) => (
                      <blockquote className="border-l-4 border-gray-600 pl-4 my-3 italic text-gray-400" {...props} />
                    ),
                    hr: ({...props}) => <hr className="border-gray-700 my-4" {...props} />,
                    table: ({...props}) => <table className="w-full border-collapse border border-gray-700 my-3" {...props} />,
                    th: ({...props}) => <th className="border border-gray-700 px-3 py-2 text-left bg-gray-800 font-semibold" {...props} />,
                    td: ({...props}) => <td className="border border-gray-700 px-3 py-2" {...props} />,
                    img: ({...props}) => <img className="max-w-full h-auto rounded-lg my-3" {...props} />,
                  } as any}
                >
                  {content}
                </ReactMarkdown>
              </div>
            )}

            {meta.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {meta.map((m, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs bg-gray-800/70 text-gray-400"
                  >
                    <span className="text-gray-600">{m.label}:</span> {m.value}
                  </span>
                ))}
              </div>
            )}

            {/* Task status selector */}
            {node.entityType === 'task' && onTaskStatusChange && (
              <div className="flex flex-wrap gap-1.5">
                {TASK_STATUSES.map(s => {
                  const isActive = (node.data as Task).status === s
                  const colors: Record<string, string> = {
                    todo: 'border-gray-500 text-gray-400',
                    pending: 'border-yellow-600 text-yellow-500',
                    backlog: 'border-gray-600 text-gray-500',
                    in_progress: 'border-blue-500 text-blue-400',
                    completed: 'border-green-500 text-green-400',
                    deprecated: 'border-orange-600 text-orange-400',
                    cancelled: 'border-red-600 text-red-400',
                  }
                  const activeBg: Record<string, string> = {
                    todo: 'bg-gray-500/20',
                    pending: 'bg-yellow-600/20',
                    backlog: 'bg-gray-600/20',
                    in_progress: 'bg-blue-500/20',
                    completed: 'bg-green-500/20',
                    deprecated: 'bg-orange-600/20',
                    cancelled: 'bg-red-600/20',
                  }
                  return (
                    <button
                      key={s}
                      onClick={() => onTaskStatusChange(s)}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${colors[s]} ${
                        isActive ? `${activeBg[s]} border-current` : 'border-transparent opacity-50 hover:opacity-80'
                      }`}
                    >
                      {s.replace('_', ' ')}
                    </button>
                  )
                })}
              </div>
            )}

            {/* Task priority slider */}
            {node.entityType === 'task' && onTaskPriorityChange && (
              <div className="flex items-center gap-3">
                <span className="text-lg" title="Low priority">😴</span>
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={(node.data as Task).priority}
                  onChange={e => onTaskPriorityChange(parseInt(e.target.value))}
                  className="flex-1 h-1.5 bg-gray-700 rounded-full appearance-none cursor-pointer accent-white [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
                />
                <span className="text-lg" title="High priority">🔥</span>
                <span className="text-xs text-gray-500 w-5 text-center font-mono">{(node.data as Task).priority}</span>
              </div>
            )}
          </>
        )}

        {/* Connections section */}
        <div className="pt-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-gray-500 text-xs uppercase tracking-wider">
              Connections ({connections.length})
            </h3>
            {onEdgeCreate && !addingConnection && (
              <button
                onClick={() => setAddingConnection(true)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-400 transition-colors"
              >
                <Plus size={14} />
                Add
              </button>
            )}
          </div>

          {addingConnection && onEdgeCreate && (
            <div className="mb-3">
              <ConnectionAdder
                currentNode={node}
                allNodes={nodes}
                onAdd={(edge) => {
                  onEdgeCreate(edge)
                  setAddingConnection(false)
                }}
                onCancel={() => setAddingConnection(false)}
              />
            </div>
          )}

          {connections.length > 0 && (
            <div className="space-y-1">
              {connections.map((conn, i) => {
                const relColor = getRelationshipColor(conn.relationship)
                return (
                  <div key={i} className="flex items-center gap-1">
                    <button
                      onClick={() => conn.node && onNodeSelect(conn.node)}
                      className="flex-1 flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-800/50 transition-colors text-left min-w-0"
                    >
                      <div
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: ENTITY_COLORS[conn.node!.entityType] }}
                      />
                      <span className="text-gray-300 text-sm flex-1 min-w-0 truncate">
                        {conn.node!.label}
                      </span>
                      <span
                        className="text-xs shrink-0 flex items-center gap-1.5"
                        style={{ color: relColor }}
                      >
                        <span className="text-gray-700">{conn.direction === 'outgoing' ? '->' : '<-'}</span>
                        {conn.relationship.toLowerCase().replace(/_/g, ' ')}
                      </span>
                    </button>
                    {onEdgeDelete && conn.edgeId != null && conn.edgeId > 0 && (
                      <button
                        onClick={() => onEdgeDelete(conn.edgeId!)}
                        className="p-1.5 text-gray-700 hover:text-red-400 transition-colors shrink-0"
                        title="Remove connection"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
